import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {injectIntl} from "react-intl";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import {
    checkModalValidationsForTestPackage, findScannableConflict, isValidEntry, PATTERNS, validateUniqueEntries
} from "../../../util/Validations";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import {CONFIG_TYPE, OPERATIONS} from "../../../util/Constants";
import {getTestConfigKey} from "../../../util/Util";
import {getLocalizedMessage} from "../../../util/Localization";

// getTestConfigKey sorts in place, so keys are taken off copies to leave the draft rows untouched.
const packageKey = (scannables) => getTestConfigKey([...scannables]);

const AddAslInductTestPackageModal = (props) => {

    const {intl, mac, open, setOpen} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext);

    // Visual controls
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false);
    const [conflict, setConflict] = useState(null);

    // Values
    const [scannables, setScannables] = useState("");
    const [sortZone, setSortZone] = useState("");

    // Error
    const [scannablesErr, setScannablesErr] = useState(false);
    const [sortZoneErr, setSortZoneErr] = useState(false);

    const inductTestPackage = {
        scannables: scannables.split(';').filter(Boolean),
        sortZone: sortZone
    }

    // Rows deleted in this draft stay in it marked DELETE. They are left alone: re-entering their package
    // ids adds a new row rather than editing one that is about to be removed.
    const draftPackages = getDraftData({stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE, subKey: mac})
        .filter((value) => value.operation !== OPERATIONS.DELETE);
    const findExistingPackage = (testPackage) => {
        const key = packageKey(testPackage.scannables);
        return draftPackages.find((value) => packageKey(value.scannables) === key);
    }

    // Editing keeps the same set of package ids, so the row being edited is not a conflict with itself.
    const getConflict = (testPackage) => {
        const key = packageKey(testPackage.scannables);
        return findScannableConflict(testPackage.scannables,
            draftPackages.filter((value) => packageKey(value.scannables) !== key));
    }

    function onCloseModal() {
        setOpen(false);
        resetInputState();
    }

    const onClickConfirm = () => {
        // An empty field splits to [], which checkModalValidationsForTestPackage would accept.
        if (!inductTestPackage.scannables.length || !checkModalValidationsForTestPackage(inductTestPackage)
            || !validateUniqueEntries(inductTestPackage.scannables)) {
            setConflict(null);
            setShow(true);
            return;
        }
        const scannableConflict = getConflict(inductTestPackage);
        if (scannableConflict) {
            setShow(false);
            setConflict(scannableConflict);
            return;
        }
        setConfigData(inductTestPackage);
        onCloseModal();
    }

    const prefill = () => {
        const existingPackage = findExistingPackage(inductTestPackage);
        if (existingPackage) {
            setEdit(true);
            setSortZoneErr(false);
            setSortZone(existingPackage.sortZone);
        } else if (edit) {
            setEdit(false);
            setSortZone("");
        }
    }

    function resetInputState() {
        setShow(false);
        setEdit(false);
        setConflict(null);
        setScannables("");
        setSortZone("");
        setScannablesErr(false);
        setSortZoneErr(false);
    }

    const setConfigData = (testPackage) => {
        if (edit) {
            editInstance({
                stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE,
                subKey: mac,
                instance: testPackage,
                keyComparator: (config1, config2) => config2.operation !== OPERATIONS.DELETE
                    && packageKey(config1.scannables) === packageKey(config2.scannables),
                valueComparator: (config1, config2) => config1.sortZone === config2.sortZone
            });
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE,
                subKey: mac,
                instance: testPackage
            });
        }
    }

    return (
        <Row>
            <Modal
                title={getLocalizedMessage({intl, stringId: "asltestpackage.addupdate"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b>{getLocalizedMessage({intl, stringId: "asltestpackage.heading"})}</b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="scannables-input">
                            {getLocalizedMessage({intl, stringId: "bay.scannables"})}
                        </Text>
                        <Input id="scannables-input"
                               value={scannables}
                               onChange={(value) => {setScannables(value.replace(/\s/g, ''))}}
                               onBlur={() => {
                                   setScannablesErr(!isValidEntry(scannables, PATTERNS.TEST_SCANNABLE));
                                   prefill();
                               }}
                               error={scannablesErr}
                               helperText={getLocalizedMessage({intl, stringId: "bay.scannables_description"})}/>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="sort-zone-input">
                            {getLocalizedMessage({intl, stringId: "bay.sortzone"})}
                        </Text>
                        <Input id="sort-zone-input"
                               value={sortZone}
                               onChange={setSortZone}
                               onBlur={() => {setSortZoneErr(!isValidEntry(sortZone, PATTERNS.SORT_ZONE))}}
                               error={sortZoneErr}
                               helperText={getLocalizedMessage({intl, stringId: "bay.sortzone_description"})}/>
                    </Column>
                </Column>
                <ModalFooter>
                    <Column>
                        {show &&
                            <Alert type="warning" size="small">
                                {getLocalizedMessage({intl, stringId: "alert.missingfields"})}
                            </Alert>}
                        {conflict &&
                            <Alert type="error" size="small">
                                {getLocalizedMessage({intl, stringId: "asltestpackage.conflict", values: conflict})}
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                        </Button>
                        <Button type="primary" size="medium" onClick={onClickConfirm}>
                            {getLocalizedMessage({intl, stringId: edit ? "modal.confirmUpdate" : "modal.confirmAdd"})}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(AddAslInductTestPackageModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asldevice/modal/AddAslInductTestPackageModal.js